import clsx from "clsx";
import { FiEye } from "react-icons/fi";
import { ThreeDots } from "react-loader-spinner";
import { Badge, Button } from "../../../../ui/index.ts";
import { displayFormatDate } from "../../../../../../utils/helpers.js";
import {
  ROW_STATUS_LABEL,
  ROW_STATUS_VARIANT,
} from "../../constants.ts";
import {
  useApproverEmployees,
} from "../../hooks/use-approver-employees.ts";
import type {
  ApprovalEmployeeRow,
  ApprovalStatusFilter,
  ApproverGroup,
  ApprovalDashboardFilters
} from "../../types.ts";

import styles from "./approval-dashboard-table.module.css";

interface CountBadgeConfig {
  key: "totalPending" | "totalApproved" | "totalRejected";
  label: string;
  variant: "warning" | "success" | "danger";
}

export interface ApproverGroupSectionProps {
  group: ApproverGroup;
  groupIdx: number;
  approverSerial: number;
  statusFilter: ApprovalStatusFilter;
  badges: CountBadgeConfig[];
  showStatusColumn: boolean;
  showOverdueColumn: boolean;
  showActionColumn: boolean;
  totalColumns: number;
  onView?: (row: ApprovalEmployeeRow) => void;
  filters: ApprovalDashboardFilters;
}

function projectName(row: ApprovalEmployeeRow): string {
  const name = row.projectName?.trim();
  return name && name.length > 0 ? name : "Miscellaneous";
}

function getEmployeeTotal(
  group: ApproverGroup,
  statusFilter: ApprovalStatusFilter,
): number {
  if (statusFilter === "PENDING") return group.totalPending ?? 0;
  if (statusFilter === "APPROVED") return group.totalApproved ?? 0;
  if (statusFilter === "REJECTED") return group.totalRejected ?? 0;
  return group.totalRecords ?? 0;
}

function formatNumber(value: number): string {
  return value.toLocaleString();
}

export function ApproverGroupSection({
  group,
  approverSerial,
  statusFilter,
  badges,
  showStatusColumn,
  showOverdueColumn,
  showActionColumn,
  totalColumns,
  onView,
  filters,
}: ApproverGroupSectionProps) {
  const initialEmployees = group.employees ?? [];
  const totalEmployees = getEmployeeTotal(group, statusFilter);
const { employees, loading, hasMore, loadMore } = useApproverEmployees(
  group.approverId,
  statusFilter,
  initialEmployees,
  totalEmployees,
  filters,
);

  const showLoadMore = hasMore;

  return (
    <>
      <tr className={styles.sectionHeaderRow}>
        <th
          className={styles.sectionHeader}
          scope="colgroup"
          colSpan={totalColumns}
        >
          <div className={styles.sectionHeaderInner}>
            <span className={styles.sectionSerial}>{approverSerial}.</span>
            <span className={styles.sectionApproverLabel}>Approver:</span>
            <span className={styles.sectionApproverName}>
              {group.approverName || "-"}
            </span>
            <span className={styles.sectionBadges}>
              {badges.map((b) => (
                <Badge
                  key={b.key}
                  variant={b.variant}
                  className={styles.countBadge}
                >
                  {b.label}: {group[b.key] ?? 0}
                </Badge>
              ))}
              <Badge variant="neutral" className={styles.countBadge}>
                Total: {group.totalRecords ?? 0}
              </Badge>
            </span>
          </div>
        </th>
      </tr>

      {loading && employees.length === 0 ? (
        <tr>
          <td
            className={clsx(styles.td, styles.innerLoadingCell)}
            colSpan={totalColumns}
          >
            <ThreeDots
              height="40"
              width="40"
              color="var(--color-accent-500)"
              visible
            />
          </td>
        </tr>
      ) : employees.length === 0 ? (
        <tr>
          <td
            className={clsx(styles.td, styles.emptyEmployees)}
            colSpan={totalColumns}
          >
            No employee rows.
          </td>
        </tr>
      ) : (
        employees.map((row, idx) => (
          <tr
            key={`${group.approverId}-${row.employeeId}-${row.timesheetDate}-${projectName(row)}-${idx}`}
            className={styles.tr}
          >
            <td className={clsx(styles.td, styles.serialCell)}>{idx + 1}</td>
            <td className={styles.td}>{row.employeeName || "-"}</td>
            <td className={styles.td}>{projectName(row)}</td>
            <td className={styles.td}>
              {displayFormatDate(row.timesheetDate)}
            </td>
            {showStatusColumn ? (
              <td className={styles.td}>
                <Badge
                  variant={ROW_STATUS_VARIANT[row.status] ?? "neutral"}
                >
                  {ROW_STATUS_LABEL[row.status] ?? row.status}
                </Badge>
              </td>
            ) : null}
            {showOverdueColumn ? (
              <td className={clsx(styles.td, styles.numberCell)}>
                {row.overdueDays ?? 0}
              </td>
            ) : null}
            {showActionColumn ? (
              <td className={clsx(styles.td, styles.actionCell)}>
                <Button
                  type="button"
                  variant="accent-ghost"
                  size="sm"
                  startIcon={<FiEye size={14} aria-hidden />}
                  onClick={() => onView?.(row)}
                >
                  View
                </Button>
              </td>
            ) : null}
          </tr>
        ))
      )}

      {showLoadMore ? (
        <tr className={styles.innerPaginationRow}>
          <td
            className={clsx(styles.td, styles.innerPaginationCell)}
            colSpan={totalColumns}
          >
            <div className={styles.innerLoadMore}>
              <span className={styles.innerPaginationRange}>
                Showing {formatNumber(employees.length)} of{" "}
                {formatNumber(totalEmployees)}
              </span>
              <button
                type="button"
                className={styles.loadMoreLink}
                disabled={loading}
                onClick={() => void loadMore()}
              >
                {loading ? (
                  <span className={styles.loadMoreLoading}>
                    <ThreeDots
                      height="20"
                      width="20"
                      color="var(--color-accent-500)"
                      visible
                    />
                    Loading...
                  </span>
                ) : (
                  "Load more"
                )}
              </button>
            </div>
          </td>
        </tr>
      ) : null}
    </>
  );
}

export default ApproverGroupSection;
